import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom';
import WizardDataContext from '../../../store/WizardDataContext';
import { usePricePlan } from '../../../store/PricePlanContext';

export default function CouponExpiration({ setDisableBtn }) {
  const { term: urlTerm } = useParams();
  const wizardCtx = useContext(WizardDataContext);
  const { wizardData, updateData } = wizardCtx;
  const expiration = wizardData.expiration;
  const isPopin = wizardData.dealSelection?.selectedDeal === 'popin';
  const popinBudget = wizardData.popinBudget;
  const { pricingData, isLoading } = usePricePlan();
  const [popinRateOptions, setPopinRateOptions] = useState([]);
  const [popinRatesLoading, setPopinRatesLoading] = useState(false);
  const [popinRatesError, setPopinRatesError] = useState(null);

  useEffect(() => {
    if (isPopin) {
      setDisableBtn(!(popinBudget && popinBudget.visitors && popinBudget.price));
    } else {
      setDisableBtn(!expiration || !expiration.duration);
    }
  }, [expiration, isPopin, popinBudget, setDisableBtn]);

  useEffect(() => {
    if (!isPopin) return;

    const controller = new AbortController();
    setPopinRatesLoading(true);
    setPopinRatesError(null);

    fetch(`${process.env.REACT_APP_API_URI}/v3/advertising/popin/rates/calculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.status === 'success' && Array.isArray(data?.data)) {
          setPopinRateOptions(data.data);
        } else {
          setPopinRateOptions([]);
          setPopinRatesError(data?.message || 'Failed to load Pop-In rates');
        }
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setPopinRateOptions([]);
        setPopinRatesError('Failed to load Pop-In rates');
      })
      .finally(() => {
        setPopinRatesLoading(false);
      });

    return () => controller.abort();
  }, [isPopin]);

  // Initialize the default selection from the URL term.
  // - If the user already has a valid selection for the current URL term → preserve it (back-navigation safe).
  // - If there is no selection, or the term changed (user picked a different plan), → default to the
  //   minimum duration of the URL term so the page reflects the plan they chose.
  useEffect(() => {
    if (isPopin) return;
    if (isLoading || !pricingData) return;

    const validTerms = ['special', 'short-term', 'mid-term', 'long-term'];

    // Resolve which term to use: URL param first, then fallback to first available
    const resolvedTerm =
      urlTerm && validTerms.includes(urlTerm) && pricingData[urlTerm]?.length
        ? urlTerm
        : validTerms.find(t => pricingData[t]?.length);

    if (!resolvedTerm) return;

    // Preserve existing selection only when it already belongs to the same resolved term
    const hasValidSelection =
      expiration?.term === resolvedTerm &&
      Number(expiration?.duration) > 0;

    if (hasValidSelection) return;

    // Apply minimum duration of the resolved term
    const termOptions = pricingData[resolvedTerm];
    const leastOption = [...termOptions].sort((a, b) => Number(a.duration) - Number(b.duration))[0];
    if (!leastOption) return;

    updateData('expiration', {
      duration: leastOption.duration,
      durationType: leastOption.unit,
      cost: leastOption.price,
      term: resolvedTerm,
      agentFee: leastOption.agentFee != null ? leastOption.agentFee : 0
    });
    setDisableBtn(false);
  }, [isLoading, pricingData, urlTerm]);

  const handleSelectPopinBudget = (option) => {
    if (!option) return;
    updateData('popinBudget', {
      visitors: option.visitors,
      price: option.price,
      popinRateId: option.id != null ? option.id : null
    });
    setDisableBtn(false);
  };

  const formatDurationLabel = (duration, unit) => {
    if (!duration || !unit) return '';
    const lowerUnit = unit.toLowerCase();
    const pluralizedUnit = duration === 1 ? lowerUnit : `${lowerUnit}s`;
    const capitalizedUnit = pluralizedUnit.charAt(0).toUpperCase() + pluralizedUnit.slice(1);
    return `${duration} ${capitalizedUnit}`;
  };

  const handleSelectOption = (termKey, option) => {
    if (!option) return;

    updateData('expiration', {
      duration: option.duration,
      durationType: option.unit,
      cost: option.price,
      term: termKey,
      agentFee: option.agentFee != null ? option.agentFee : 0
    });
    setDisableBtn(false);
  };

  const termOrder = ['special', 'short-term', 'mid-term', 'long-term'];
  const termTitles = {
    special: 'Special',
    'short-term': 'Short Term',
    'mid-term': 'Mid Term',
    'long-term': 'Long Term'
  };

  return (
    <>
      <div className="step4-container">
        <div className="step4-header">
          <h1>{isPopin ? 'Select Pay-Per-Visit Budget' : 'Set Coupon Expiration Date'}</h1>
          <h3>
            {isPopin
              ? 'Please tell us how many visitors you would like to receive'
              : 'Please tell us how long the coupon will be available'}
          </h3>
        </div>
        <div className="expiration-grid">
          {isPopin && (
            <div className="expiration-group">
              <div className="expiration-group__grid" role="radiogroup" aria-label="Pop-In budget options">
                {popinRatesLoading && (
                  <p className="expiration-grid__loading">Loading Pop-In budget options...</p>
                )}
                {!popinRatesLoading && popinRatesError && (
                  <p className="expiration-grid__empty">{popinRatesError}</p>
                )}
                {!popinRatesLoading && !popinRatesError && popinRateOptions.map((option) => {
                  const isSelected =
                    popinBudget &&
                    Number(popinBudget.visitors) === Number(option.visitors) &&
                    Number(popinBudget.price) === Number(option.price);

                  return (
                    <button
                      key={option.id != null ? option.id : `${option.visitors}-${option.price}`}
                      type="button"
                      className={`expiration-card${isSelected ? ' expiration-card--selected' : ''}`}
                      onClick={() => handleSelectPopinBudget(option)}
                      role="radio"
                      aria-checked={isSelected}
                    >
                      <div className="expiration-card__duration">
                        {option.visitors} Visitors
                      </div>
                      <div className="expiration-card__price">
                        R{option.price}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="expiration-grid__empty" style={{ marginTop: 12 }}>
                Your offer will only expire once the allocated budget is exhausted. Upon expiration you will receive an email with instructions to top-up your offer.
              </p>
            </div>
          )}
          {isLoading && (
            <p className="expiration-grid__loading">Loading expiration options...</p>
          )}

          {!isPopin && !isLoading && pricingData && termOrder.map((termKey) => {
            const termOptions = pricingData[termKey];
            if (!termOptions || termOptions.length === 0) return null;

            return (
              <div key={termKey} className="expiration-group">
                <h4 className="expiration-group__title">{termTitles[termKey] || termKey}</h4>
                <div className="expiration-group__grid" role="radiogroup" aria-label={`${termTitles[termKey]} coupon durations`}>
                  {termOptions.map((option) => {
                    const isSelected =
                      expiration &&
                      expiration.term === termKey &&
                      Number(expiration.duration) === Number(option.duration);

                    return (
                      <button
                        key={`${termKey}-${option.duration}`}
                        type="button"
                        className={`expiration-card${isSelected ? ' expiration-card--selected' : ''}`}
                        onClick={() => handleSelectOption(termKey, option)}
                        role="radio"
                        aria-checked={isSelected}
                      >
                        <div className="expiration-card__duration">
                          {formatDurationLabel(option.duration, option.unit)}
                        </div>
                        <div className="expiration-card__price">
                          R{option.price}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {!isPopin && !isLoading && (!pricingData || Object.keys(pricingData).length === 0) && (
            <p className="expiration-grid__empty">
              No expiration options are available right now. Please try again later.
            </p>
          )}
        </div>
      </div>
    </>
  )
}
